/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value
 * @desc Service for SmartTable ProtocolValue
 */

import { arrayMove } from '@dnd-kit/sortable';
import type {
  CellStyle,
  Column,
  ColumnApi,
  EditableCallbackParams,
  HeaderClassParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { selectHasAccess } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { EntitySearchActionBar as ActionBar } from 'labstep-web/components/Entity/Search/ActionBar';
import { BulkAction } from 'labstep-web/components/Entity/Search/BulkActions';
import ProtocolValueActionCreate from 'labstep-web/components/ProtocolValue/Action/Create';
import { ProtocolValueVariableActionSetItemsContainer } from 'labstep-web/components/ProtocolValue/Variable/Action/SetItems/Container';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import {
  toggleColumnVisible,
  updateValue,
} from 'labstep-web/core/DataGrid/utils';
import { Flex } from 'labstep-web/core/Flex';
import type { ISortableProps } from 'labstep-web/core/Sortable/types';
import { useToolPanelContext } from 'labstep-web/grid/Index/components/ToolPanel/context';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import type { CellState } from 'labstep-web/grid/SmartTableProtocolValue';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { ToolPanelState } from 'labstep-web/grid/SmartTableProtocolValue/components/ToolPanel';
import type {
  RangeType,
  StatusBarProps,
} from 'labstep-web/grid/SmartTableProtocolValue/types';
import {
  GridService,
  type SectionType,
} from 'labstep-web/grid/services/grid.service';
import type { APICallOptions } from 'labstep-web/models';
import type { Entity } from 'labstep-web/models/entity.model';
import { EntityView } from 'labstep-web/models/entity-view.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { denormalize } from 'labstep-web/services/normalizr';
import {
  type EntityViewParameters,
  PostFilterComparison,
  type PostFilterNodeType,
  PostFilterOperator,
} from 'labstep-web/services/postFilter/constants';
import schema from 'labstep-web/services/schema';
import rules from 'labstep-web/services/validation/rules';
import {
  createEntity,
  updateEntity,
} from 'labstep-web/state/actions/entity';
import { showToast } from 'labstep-web/state/actions/ui';
import { getNestedEntitiesObject } from 'labstep-web/state/selectors/helpers';
import store from 'labstep-web/state/store';
import { cloneDeep } from 'lodash';

export const PINNED_COL_IDS = ['action_menu', 'bulk_select', 'name'];

export const userFilteStatusAvailable = {
  type: PostFilterOperator.and,
  predicates: [
    {
      value: 'available',
      attribute: 'status',
      comparison: PostFilterComparison.eq,
    },
  ],
};

export const getSystemFilterExperimentGuid = (
  experimentGuid: string,
) => ({
  path: 'experiment',
  type: PostFilterOperator.and,
  predicates: [
    {
      value: experimentGuid,
      attribute: 'guid',
      comparison: PostFilterComparison.eq,
    },
  ],
});

export const getSystemFilterProtocolGuid = (
  protocolGuid: string,
) => ({
  path: 'protocol',
  type: PostFilterOperator.and,
  predicates: [
    {
      value: protocolGuid,
      attribute: 'guid',
      comparison: PostFilterComparison.eq,
    },
  ],
});

export const getSystemFilterResourceGuid = (
  resourceGuid: string,
) => ({
  path: 'resource',
  type: PostFilterOperator.and,
  predicates: [
    {
      value: resourceGuid,
      attribute: 'guid',
      comparison: PostFilterComparison.eq,
    },
  ],
});

export const getSystemFilterTemplateGuid = (
  templateGuid: string,
) => ({
  path: 'template',
  type: PostFilterOperator.and,
  predicates: [
    {
      value: templateGuid,
      attribute: 'guid',
      comparison: PostFilterComparison.eq,
    },
  ],
});

export const systemFilterNoTemplate = {
  path: 'template',
  type: PostFilterOperator.and,
  predicates: [
    {
      attribute: 'guid',
      comparison: PostFilterComparison.not_exists,
    },
  ],
};

export interface IEntityDataGridToolPanelSectionItemProps {
  column: ColumnWithId;
  section: SectionType;
  onToggleColumnVisible: (colId?: string) => void;
}

export interface IEntityDataGridToolPanelSectionProps {
  columns: Column[];
  section: SectionType;
  onToggleColumnVisible: IEntityDataGridToolPanelSectionItemProps['onToggleColumnVisible'];
  onSortColumns: (columns: Column[]) => void;
}

export type ColumnWithId = Column & {
  getId: () => string;
};

export type ItemWithColumn = {
  id: string;
  column: ColumnWithId;
};

export const PROTOCOL_VALUE_FIELDS = [
  'amount',
  'resource_item',
  'resource',
  'resource_location',
];

export class GridSmartTableProtocolValueService extends GridService {
  public static createMetadata = (
    params: any,
    experimentOrProtocol: Experiment | Protocol,
    parentEntityClass: string,
    metadataType: MetadataType,
    metadataLabel: string,
    protocolValueName?: string,
    values: Record<string, unknown> = {},
    onSuccess?: () => void,
  ): boolean => {
    const metadataThreadId =
      GridSmartTableProtocolValueService.getMetadataThreadId(
        experimentOrProtocol,
        params.node.data,
        parentEntityClass,
        protocolValueName,
      );

    if (!metadataThreadId) {
      return false;
    }

    const body: Record<string, unknown> = {
      protocol_condition_id: params.node.data.guid,
      metadata_thread_id: metadataThreadId,
      type: metadataType,
      label: metadataLabel,
      ...values,
    };

    return GridSmartTableProtocolValueService.createEntity(
      params,
      Metadata.entityName,
      body,
      MetadataThread.entityName,
      metadataThreadId,
      onSuccess,
    );
  };

  public static isValid = (
    entityName: string,
    body: Record<string, unknown>,
  ): boolean => {
    for (const field of Object.keys(body)) {
      const entityRules = rules[entityName];
      if (
        entityRules &&
        typeof entityRules === 'object' &&
        field in entityRules
      ) {
        const rule = (entityRules as Record<string, any>)[field];
        if (rule && typeof rule.isValidSync === 'function') {
          if (!rule.isValidSync(body[field])) {
            this.triggerValidationError(field);
            return false;
          }
        }
      }
    }
    return true;
  };

  public static triggerValidationError = (field: string): void => {
    showToast({
      type: 'error',
      message: `Invalid value for ${field}`,
      options: {
        header: 'Validation error',
        timeout: 6000,
      },
    });
  };

  public static createEntity = (
    params: any,
    entityName: string,
    body: Record<string, unknown>,
    parentName?: string,
    parentId?: string | number,
    onSuccess?: () => void,
  ): boolean => {
    if (!this.isValid(entityName, body)) {
      return false;
    }
    this.setCellState(params, 'loading');

    const action = createEntity(
      entityName,
      body,
      parentName,
      parentId,
      undefined,
      GridSmartTableProtocolValueService.getOptions(
        params,
        onSuccess,
      ) as any,
      undefined,
      true,
    );
    store.dispatch(action);

    return true;
  };

  public static updateEntity = (
    params: any,
    entityName: string,
    id: string | number,
    body: Record<string, unknown>,
    onSuccess?: () => void,
  ): boolean => {
    if (!this.isValid(entityName, body)) {
      return false;
    }
    this.setCellState(params, 'loading');

    const action = updateEntity(entityName, id, body, {
      ...(GridSmartTableProtocolValueService.getOptions(
        params,
        onSuccess,
      ) as any),
      optimistic: true,
    });
    store.dispatch(action);

    return true;
  };

  public static startEditingCell = (params: any): void => {
    params.api.startEditingCell({
      rowIndex: params.node!.rowIndex!,
      colKey: params.column.getColId(),
    });
  };

  public static isEditable = (
    params: EditableCallbackParams,
  ): boolean => {
    const colDef = params.column.getColDef();
    return (
      !!colDef &&
      typeof colDef.editable === 'function' &&
      colDef.editable(params)
    );
  };

  public static getCellStyle = (
    params: any,
    experimentOrProtocol: Experiment | Protocol,
    protocolConditionChildEntityName: string,
    nameOrLabel: string,
    parentEntityClass?: string,
  ): CellStyle | null => {
    if (!params.node) {
      return null;
    }

    if (parentEntityClass === 'experiment') {
      return {
        backgroundColor: '#eff2f7',
      };
    }

    if (!this.isEditable(params)) {
      return {
        backgroundColor: '#f5f5f5',
      };
    }

    const cellState =
      GridSmartTableProtocolValueService.getCellState(params);
    if (cellState === 'success') {
      return {
        background: '#ffffff',
      };
    }
    if (cellState === 'loading') {
      return {
        background:
          "#ffffff url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cg fill='%23666'%3E%3Ccircle cx='8' cy='8' r='6' fill='none' stroke='%23666' stroke-width='1' stroke-linecap='round' stroke-dasharray='9.42 9.42' opacity='0.8'%3E%3CanimateTransform attributeName='transform' type='rotate' values='0 8 8;360 8 8' dur='1s' repeatCount='indefinite'/%3E%3C/circle%3E%3C/g%3E%3C/svg%3E\") no-repeat right 8px center",
        backgroundSize: 'auto, 16px 16px',
      };
    }

    return null;
  };

  public static getCellState = (params: any): CellState | null => {
    if (
      params.node &&
      params.node.data &&
      params.context &&
      params.context.getCellState
    ) {
      const entityId = params.node.data.idAttr;
      const colId = params.column.getColId();
      return params.context.getCellState(entityId, colId);
    }
    return null;
  };

  public static setCellState = (
    params: any,
    state: CellState | null,
  ): void => {
    if (
      params.node &&
      params.node.data &&
      params.context &&
      params.context.setCellState
    ) {
      const entityId = params.node.data.idAttr;
      const colId = params.column.getColId();
      params.context.setCellState(entityId, colId, state);
      GridSmartTableProtocolValueService.refreshRow(params);
    }
  };

  public static getEntityFromState = (
    experimentOrProtocol: Experiment | Protocol,
  ) => {
    const state = store.getState();

    return denormalize(
      state.entities[experimentOrProtocol.entityName].byId[
        experimentOrProtocol.id
      ],
      schema[experimentOrProtocol.entityName as keyof typeof schema],
      getNestedEntitiesObject(state),
    );
  };

  /**
   * Returns a function bound to the onSortEnd prop of dnd-kit sortable
   * @param items ItemWithColumn array
   * @param fn Function to call when sorting is complete
   * @returns Column array
   */
  public static onSortEndColumns(
    items: ItemWithColumn[],
    fn: (columns: Column[]) => void,
  ): ISortableProps<ItemWithColumn>['onSortEnd'] {
    return ({ oldIndex, newIndex }) => {
      const newItems = arrayMove(items, oldIndex, newIndex);
      const newColumns =
        GridSmartTableProtocolValueService.itemsToColumns(newItems);
      fn(newColumns);
    };
  }

  /**
   * Creates an array of objects with the column and its id
   * For use with dnd-kit sortable
   * @param columns Column array
   * @returns ItemWithColumn array
   */
  public static columnsToItems(columns: Column[]): ItemWithColumn[] {
    return columns
      .filter(
        (column) =>
          column.getId() && GridNewIndexService.isHideable(column),
      )
      .map((column) => ({
        id: column.getId(),
        column,
      }));
  }

  /**
   * Creates an array of columns from an array of objects with the column and its id
   * @param items ItemWithColumn array
   * @returns Column array
   */
  public static itemsToColumns(items: ItemWithColumn[]): Column[] {
    return items.map((item) => item.column);
  }

  public static isHideable(column: HeaderClassParams['column']) {
    if (!column) {
      return false;
    }
    return (
      !column.isPinned() &&
      !column.getColDef().lockPinned &&
      !column.getColDef().lockVisible
    );
  }

  public static metadataValueGetter = (
    metadatas: Metadata[],
    label: string,
  ) => {
    const foundMetadata = metadatas
      ? metadatas.find(
          (dataMetadata: Metadata) =>
            dataMetadata.label === label ||
            dataMetadata.variable_template?.label === label,
        )
      : null;

    if (!foundMetadata) {
      return '';
    }
    if (foundMetadata.type === 'file') {
      return foundMetadata.files
        .map((file) => foundMetadata.display_value)
        .join(', ');
    }

    return foundMetadata.display_value;
  };

  public static metadataCellRenderer = (
    metadatas: Metadata[],
    label: string,
  ) => {
    const foundMetadata = metadatas
      ? metadatas.find(
          (dataMetadata: Metadata) =>
            dataMetadata.label === label ||
            dataMetadata.variable_template?.label === label,
        )
      : null;

    if (!foundMetadata) {
      return '';
    }

    return foundMetadata.display_value || '';
  };

  public static showListSection = (
    labels: string[],
    searchQuery: string | null | undefined,
  ) =>
    !searchQuery ||
    searchQuery.length === 0 ||
    labels
      .map((label) => label.includes(searchQuery.toLowerCase()))
      .includes(true);

  public static showListItem = (
    label: string,
    searchQuery: string | null | undefined,
  ) =>
    !searchQuery ||
    searchQuery.length === 0 ||
    label.includes(searchQuery.toLowerCase());

  public getProtocolValues = (protocolValueName: string) => {
    const { api } = this._agGrid;

    const protocolValues: ProtocolValue[] = [];
    api.forEachNode((node: any) => {
      if (
        node.data &&
        node.data instanceof ProtocolValue &&
        node.data.protocol_values_variable
      ) {
        protocolValues.push(
          ...node.data.protocol_values_variable.filter(
            (p: ProtocolValue) => p.name === protocolValueName,
          ),
        );
      }
    });
    return protocolValues;
  };

  public static getProtocolValueProtocolValue = (
    protocolCondition: ProtocolValue,
    experimentOrProtocol: Experiment | Protocol,
    name: string,
  ) => {
    const entityFromState = this.getEntityFromState(
      experimentOrProtocol,
    );

    return entityFromState.protocol_values
      ? entityFromState.protocol_values.find(
          (dataProtocolValue: ProtocolValue) =>
            dataProtocolValue.name === name &&
            dataProtocolValue.variable_template_guid !== null &&
            dataProtocolValue.protocol_condition.guid ===
              protocolCondition.guid,
        )
      : null;
  };

  public static getProtocolValue = (
    experimentOrProtocol: Experiment | Protocol,
    name: string,
  ): ProtocolValue | null => {
    const entityFromState = this.getEntityFromState(
      experimentOrProtocol,
    );
    return entityFromState.protocol_values
      ? entityFromState.protocol_values.find(
          (dataProtocolValue: ProtocolValue) =>
            dataProtocolValue.name === name &&
            dataProtocolValue.variable_template_guid === null,
        )
      : null;
  };

  public static getMetadataThreadId = (
    experimentOrProtocol: Experiment | Protocol,
    protocolCondition: ProtocolValue,
    parentEntityClass: string,
    protocolValueName?: string,
  ) => {
    return this.getMetadataParent(
      experimentOrProtocol,
      protocolCondition,
      parentEntityClass,
      protocolValueName,
    )!.metadata_thread.guid;
  };

  public static getProtocolValueTemplate = (
    experimentOrProtocol: Experiment | Protocol,
    name: string,
  ): ProtocolValue | null => {
    const entityInState = this.getEntityFromState(
      experimentOrProtocol,
    );

    const variableConditionTemplate =
      entityInState.protocol_values.find(
        (variable: ProtocolValue) => variable.name === name,
      ) || null;

    return (
      (variableConditionTemplate &&
        entityInState.protocol_values.find(
          (variable: ProtocolValue) =>
            variable.guid === variableConditionTemplate?.guid,
        )) ||
      null
    );
  };

  public toggleColumnVisible = (
    params: { columnApi: ColumnApi; column?: Column },
    colId: string,
  ): void => {
    toggleColumnVisible(params, colId);
  };

  public addColumnDef(colDef: SmartTableColDef): void {
    const currentColDefs = this.getColumnDefs();

    if (currentColDefs.find((col) => col.colId === colDef.colId)) {
      this.setColumnHide(colDef.colId, false);

      return;
    }

    this.setColumnDefs([...this.getColumnDefs(), colDef]);
  }

  public addColumnDefs(colDefs: SmartTableColDef[]): void {
    const currentColDefs = this.getColumnDefs();
    colDefs.forEach((colDef) => {
      if (currentColDefs.find((col) => col.colId === colDef.colId)) {
        this.setColumnHide(colDef.colId, false);
        return;
      }
    });

    this.setColumnDefs([...this.getColumnDefs(), ...colDefs]);
  }

  public updateEntityViewColIds(entityView: EntityView): void {
    updateValue(
      { entityName: EntityView.entityName, id: entityView.id },
      'column_definition_ids',
      this.getColumnDefinitionIds(),
    );
  }

  public deleteInventoryFieldColDefs(
    protocolValue: ProtocolValue,
    entityView: EntityView,
  ): void {
    const newColumnDefinitionIds = this.getColumnDefs().filter(
      (colDef) => {
        const split = colDef.colId?.split(':');
        if (split && split.length >= 4) {
          const type = split[0];
          const name =
            type === 'protocol_value' ? split[2] : split[4];

          return name !== protocolValue.reportingName;
        }
        return false;
      },
    );
    this.setColumnDefs(newColumnDefinitionIds);
    this.updateEntityViewColIds(entityView);
  }

  public deleteDataFieldColDefs(
    metadata: Metadata,
    entityView: EntityView,
  ): void {
    const newColumnDefinitionIds = this.getColumnDefs().filter(
      (colDef) =>
        !(
          (colDef.colId?.startsWith('metadata:experiment') ||
            colDef.colId?.startsWith('metadata:protocol')) &&
          colDef?.colId?.endsWith(`:${metadata.displayName}`)
        ),
    );
    this.setColumnDefs(newColumnDefinitionIds);
    this.updateEntityViewColIds(entityView);
  }

  public updateColDef(colDefs: SmartTableColDef[]): void {
    const newColdefs = this.getColumnDefs().filter(
      (coldef) => !colDefs.find((col) => col.colId === coldef.colId),
    );
    this.setColumnDefs([...newColdefs, ...colDefs]);
  }

  public static updateColIdsProtocolValueName(
    colDefs: SmartTableColDef[],
    protocolValueName: string,
    newProtocolValueName: string,
    entityView: EntityView,
  ): void {
    const columnDefinitionIds = colDefs.map(
      (colDef: any) => colDef.colId,
    );
    const newColumnDefinitionIds = columnDefinitionIds.map((id) => {
      const parts = id.split(':');
      if (
        parts.length === 3 &&
        parts[0] === 'protocol_value' &&
        parts[1] === protocolValueName
      ) {
        return `protocol_value:${newProtocolValueName}:${parts[2]}`;
      }
      return id;
    });
    updateValue(
      {
        entityName: EntityView.entityName,
        id: entityView.id,
      },
      'column_definition_ids',
      newColumnDefinitionIds,
    );
  }

  public static getEditable = (
    params: EditableCallbackParams,
  ): boolean => {
    if (!params.node?.data) {
      return true;
    }

    return !!selectHasAccess(
      store.getState(),
      params.node.data.entityName,
      params.node.data.idAttr,
      Action.edit,
    );
  };

  public static getMetadataEditable = (
    params: EditableCallbackParams,
    experimentOrProtocol: Experiment | Protocol,
    metadataType: string,
    metadataLabel: string,
    parentEntityClass: string,
    protocolValueName?: string,
  ): boolean => {
    if (!params.node?.data) {
      return true;
    }

    if (protocolValueName) {
      const foundProtocolValue =
        GridSmartTableProtocolValueService.getProtocolValueProtocolValue(
          params.node.data,
          experimentOrProtocol,
          protocolValueName,
        );
      if (
        parentEntityClass === ResourceItem.entityName &&
        !foundProtocolValue?.resourceItem
      ) {
        return false;
      }
      if (
        parentEntityClass === Resource.entityName &&
        !foundProtocolValue?.resource &&
        !foundProtocolValue?.resourceItem
      ) {
        return false;
      }

      if (parentEntityClass === 'experiment') {
        return false;
      }

      const metadataThreadId =
        GridSmartTableProtocolValueService.getMetadataThreadId(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          protocolValueName,
        );

      if (!metadataThreadId) {
        return false;
      }
    }

    if (experimentOrProtocol.isLocked) {
      return false;
    }

    return this.getEditable(params);
  };

  public static getSelectedColumn(ranges: RangeType[]): {
    protocolValues: (ProtocolValue | ProtocolValue)[];
  } | null {
    // Only single selection is supported
    if (ranges.length !== 1) {
      return null;
    }

    const range = ranges[0];
    if (range.columns.length !== 1) {
      return null;
    }

    const colId = range.columns[0].getColId();
    if (!colId) {
      return null;
    }

    const parts = colId.split(':');
    if (parts.length < 3) {
      return null;
    }

    const [entityName, type] = parts;

    // Validate column ID format
    if (
      entityName !== ProtocolValue.entityName ||
      type !== 'resource_item'
    ) {
      return null;
    }

    return range.entities[0];
  }

  public static getStatusBarChildren: StatusBarProps =
    ({ experimentOrProtocol }) =>
    ({ ranges, api }): JSX.Element => {
      const toolPanelContext = useToolPanelContext();
      const column =
        GridSmartTableProtocolValueService.getSelectedColumn(ranges);
      if (!column || column.protocolValues.length <= 1) {
        return (
          <Flex vAlignContent="center" spacing="gap">
            <ActionComponent
              icon="plus"
              type="text"
              text="Add Inventory Field"
              onClick={() => {
                if (toolPanelContext) {
                  toolPanelContext.setToolPanelState(
                    ToolPanelState.protocol_value,
                  );
                }
                if (api) {
                  api.setSideBarVisible(true);
                }
              }}
            />
            <ActionBar
              entityName={ProtocolValue.entityName}
              bulkActions={[BulkAction.delete]}
            />
          </Flex>
        );
      }
      return (
        <ProtocolValueVariableActionSetItemsContainer>
          {(containerProps): React.ReactElement => (
            <Flex vAlignContent="center" spacing="gap">
              <ProtocolValueActionCreate
                protocol={experimentOrProtocol}
                actionComponentProps={{
                  type: 'text',
                  icon: 'plus',
                }}
              />
              {/* {column.variableTemplate.is_input && (
                <ProtocolValueVariableActionSetItems
                  protocolValueTemplate={column.variableTemplate}
                  protocolValuesOrConditions={column.protocolValues}
                  {...containerProps}
                />
              )} */}
            </Flex>
          )}
        </ProtocolValueVariableActionSetItemsContainer>
      );
    };

  public static getExperimentOrProtocolMetadata(
    experimentOrProtocol: Experiment | Protocol,
    metadataLabel: string,
    metadataType: MetadataType,
  ): Metadata | null {
    if (!experimentOrProtocol.metadata_thread?.metadatas) {
      return null;
    }

    return (
      experimentOrProtocol.metadata_thread.metadatas.find(
        (metadata: Metadata) =>
          metadata.type === metadataType &&
          metadata.label === metadataLabel,
      ) || null
    );
  }

  public static getProtocolValueMetadata(
    experimentOrProtocol: Experiment | Protocol,
    protocolCondition: ProtocolValue,
    parentEntityClass: string,
    metadataLabel: string,
    metadataType: MetadataType,
    protocolValueName?: string,
  ): Metadata | null {
    const metadataParent =
      GridSmartTableProtocolValueService.getMetadataParent(
        experimentOrProtocol,
        protocolCondition,
        parentEntityClass,
        protocolValueName,
      );

    if (!metadataParent?.metadata_thread?.metadatas) {
      return null;
    }

    return (
      metadataParent.metadata_thread.metadatas.find(
        (metadata: Metadata) =>
          (protocolValueName ||
            metadata.protocol_condition?.guid ===
              protocolCondition.guid ||
            (metadata.protocol_condition as any) === // In case of denormalized data
              protocolCondition.guid) &&
          metadata.type === metadataType &&
          metadata.label === metadataLabel,
      ) || null
    );
  }

  public static getMetadataParent(
    experimentOrProtocol: Experiment | Protocol,
    protocolCondition: ProtocolValue,
    parentEntityClass: string,
    protocolValueName?: string,
  ): Experiment | Protocol | Resource | ResourceItem | null {
    if (
      parentEntityClass === Experiment.entityName ||
      parentEntityClass === Protocol.entityName
    ) {
      return GridSmartTableProtocolValueService.getEntityFromState(
        experimentOrProtocol,
      );
    }

    if (
      parentEntityClass === Resource.entityName &&
      protocolValueName
    ) {
      const protocolValue =
        GridSmartTableProtocolValueService.getProtocolValueProtocolValue(
          protocolCondition,
          experimentOrProtocol,
          protocolValueName,
        );
      return (
        protocolValue?.resourceItem?.resource ||
        protocolValue?.resource ||
        null
      );
    }

    if (
      parentEntityClass === ResourceItem.entityName &&
      protocolValueName
    ) {
      const protocolValue =
        GridSmartTableProtocolValueService.getProtocolValueProtocolValue(
          protocolCondition,
          experimentOrProtocol,
          protocolValueName,
        );
      return protocolValue?.resourceItem || null;
    }

    return null;
  }

  public static getLegacyConditionTableColIds = (
    entity: Experiment | Protocol,
  ) => {
    const defaultColdefs = ['row_number'];

    if (entity?.protocol_condition_variables) {
      entity.protocol_condition_variables
        .filter(
          (variable) =>
            !variable.variable_template && variable.is_input === true,
        )
        .forEach((variable: any) => {
          if (variable instanceof ProtocolValue) {
            defaultColdefs.push(
              `protocol_value:${variable.name}:amount`,
              `protocol_value:${variable.name}:resource_item`,
            );
          }
        });

      entity.protocol_condition_variables
        .filter(
          (variable) =>
            !variable.variable_template && variable.is_input === true,
        )
        .forEach((variable: any) => {
          if (variable instanceof Metadata) {
            defaultColdefs.push(
              `metadata:experiment:${variable.type}:${variable.label}`,
            );
          }
        });

      entity.protocol_condition_variables
        .filter(
          (variable) =>
            !variable.variable_template &&
            variable.is_input === false,
        )
        .forEach((variable: any) => {
          if (variable instanceof ProtocolValue) {
            defaultColdefs.push(
              `protocol_value:${variable.name}:amount`,
              `protocol_value:${variable.name}:resource_item`,
            );
          }
        });
      entity.protocol_condition_variables
        .filter(
          (variable) =>
            !variable.variable_template &&
            variable.is_input === false,
        )
        .forEach((variable: any) => {
          if (variable instanceof Metadata) {
            defaultColdefs.push(
              `metadata:experiment:${variable.type}:${variable.label}`,
            );
          }
        });
    }

    return defaultColdefs;
  };

  public static getDefaultVariableColIds = (
    entity: Experiment | Protocol,
  ) => {
    const defaultColdefs = ['row_number'];

    if (entity?.protocol_condition_variables) {
      entity.protocol_condition_variables
        .filter((variable) => !variable.variable_template)
        .forEach((variable: any) => {
          if (variable instanceof Metadata) {
            defaultColdefs.push(
              `metadata:experiment:${variable.type}:${variable.label}`,
            );
          }
          if (variable instanceof ProtocolValue) {
            defaultColdefs.push(
              `protocol_value:${variable.name}:amount`,
              `protocol_value:${variable.name}:resource_item`,
            );
          }
        });
    }

    return defaultColdefs;
  };

  public static getDefaultProtocolValueVariableColIds = (
    protocolValue: ProtocolValue,
  ) => {
    return [
      'row_number',
      `protocol_value:${protocolValue.name}:amount`,
      `protocol_value:${protocolValue.name}:resource_item`,
    ];
  };

  public static getDefaultMetadataVariableColIds = (
    metadata: Metadata,
  ) => {
    return [
      'row_number',
      `metadata:experiment:${metadata.type}:${metadata.label}`,
    ];
  };

  public static getMetadataParentPrefix = (
    parentEntityClass: string,
  ): string => {
    if (parentEntityClass === Resource.entityName) {
      return ' - Resource';
    }
    if (parentEntityClass === ResourceItem.entityName) {
      return ' - Item';
    }
    return '';
  };

  public static refreshColumn = (
    params:
      | ValueSetterParams
      | CellRendererParams<Entity>
      | CellEditorParams<Entity>,
  ): void => {
    setTimeout(() => {
      if (params.node) {
        params.api.refreshCells({
          columns: [params.column.getColId()],
          force: true,
          suppressFlash: true,
        });
      }
    }, 100);
  };

  public static refreshCell = (
    params:
      | ValueSetterParams
      | CellRendererParams<Entity>
      | CellEditorParams<Entity>,
  ): void => {
    setTimeout(() => {
      if (params.node) {
        this.refreshRow(params);
      }
    }, 100);
  };

  public static refreshRow = (
    params:
      | ValueSetterParams
      | CellRendererParams<Entity>
      | CellEditorParams<Entity>,
  ): void => {
    if (params.node) {
      params.api.redrawRows({ rowNodes: [params.node] });
    }
  };

  public static refreshGrid = (
    params:
      | ValueSetterParams
      | CellRendererParams<Entity>
      | CellEditorParams<Entity>,
  ): void => {
    if (params.node) {
      params.api.refreshCells({
        force: true,
        suppressFlash: true,
      });
    }
  };

  public static getOptions = (
    params:
      | ValueSetterParams
      | CellRendererParams<Entity>
      | CellEditorParams<Entity>,
    onSuccess?: () => void,
  ): APICallOptions => {
    return {
      onSuccess: ({ response }): void => {
        const idOrGuid = response?.result || response;
        if (
          idOrGuid &&
          typeof idOrGuid === 'string' &&
          idOrGuid[0] !== '-'
        ) {
          GridSmartTableProtocolValueService.setCellState(
            params,
            'success',
          );
        }

        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
        }, 200);

        setTimeout(() => {
          GridSmartTableProtocolValueService.setCellState(
            params,
            null,
          );
        }, 400);
      },
    };
  };

  public static getEntityViewProtocolValueNameFilter = (
    entityView: EntityView,
  ): PostFilterNodeType | null => {
    if (
      !entityView.parameters?.filter ||
      entityView.parameters.filter.length !== 2 ||
      !entityView.parameters.filter[1].predicates
    ) {
      return null;
    }

    for (const filter of entityView.parameters.filter[1].predicates) {
      if (
        filter.predicates &&
        filter.predicates.length > 0 &&
        filter.predicates[0].attribute === 'name'
      ) {
        return filter.predicates[0];
      }
    }

    return null;
  };

  public static selectProtocolValue = (
    entityView: EntityView,
    protocolValue: ProtocolValue,
  ) => {
    const parameters =
      GridSmartTableProtocolValueService.pushEntityViewProtocolValueNameFilter(
        entityView,
        protocolValue.reportingName,
      );
    updateValue(entityView, 'parameters', parameters);
  };

  public static unselectProtocolValue = (
    entityView: EntityView,
    protocolValue: ProtocolValue,
  ) => {
    const parameters =
      GridSmartTableProtocolValueService.removeEntityViewProtocolValueNameFilter(
        entityView,
        protocolValue.reportingName,
      );
    updateValue(entityView, 'parameters', parameters);
  };

  public static pushEntityViewProtocolValueNameFilter = (
    entityView: EntityView,
    protocolValueName: string,
  ): EntityViewParameters | null | undefined => {
    if (
      !entityView.parameters?.filter ||
      entityView.parameters.filter.length !== 2
    ) {
      return entityView.parameters;
    }
    const parameters = cloneDeep(entityView.parameters);

    if (
      !parameters.filter?.[1] ||
      !parameters.filter?.[1].predicates
    ) {
      return entityView.parameters;
    }

    for (const filter of parameters.filter[1].predicates) {
      if (
        filter.predicates &&
        filter.predicates.length > 0 &&
        filter.predicates[0].attribute === 'name'
      ) {
        if (!filter.predicates[0].value) {
          filter.predicates[0].value = [];
        }
        (filter.predicates[0].value as string[]).push(
          protocolValueName,
        );
      }
    }

    return parameters;
  };

  public static removeEntityViewProtocolValueNameFilter = (
    entityView: EntityView,
    protocolValueName: string,
  ): EntityViewParameters | null | undefined => {
    if (
      !entityView.parameters?.filter ||
      entityView.parameters.filter.length !== 2
    ) {
      return entityView.parameters;
    }

    const parameters = cloneDeep(entityView.parameters);

    const systemFilter = parameters.filter?.[1];
    if (!systemFilter || !systemFilter.predicates) {
      return entityView.parameters;
    }

    for (const filter of systemFilter.predicates) {
      if (
        filter.predicates &&
        filter.predicates.length > 0 &&
        filter.predicates[0].attribute === 'name'
      ) {
        if (!filter.predicates[0].value) {
          filter.predicates[0].value = [];
        }
        filter.predicates[0].value = (
          filter.predicates[0].value as string[]
        ).filter((item: string) => item !== protocolValueName);
      }
    }

    return parameters;
  };
}
